import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { Plus, Package, Edit, Trash2 } from 'lucide-react';
import DataTable from '../../../components/ui/DataTable';
import toast from 'react-hot-toast';
import { masterMenuModuleService } from '../../../api/masterMenuModuleService';
import { extractErrorMessage } from '../../../utils/errorHandler';
import CreateModuleModal from '../components/CreateModuleModal';
import ConfirmationModal from '../../../components/modals/ConfirmationModal';

/**
 * Module Creation Screen
 * 
 * Displays a table of existing modules with options to create, edit, and delete.
 * 
 * @component
 */
const ModuleCreation = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [moduleToDelete, setModuleToDelete] = useState(null);

  // Fetch modules on component mount
  useEffect(() => {
    loadModules();
  }, []);

  const loadModules = async () => {
    try {
      setLoading(true);
      // Use getModulesIncludingInactive to get both active and inactive modules
      const response = await masterMenuModuleService.getModulesIncludingInactive();
      const modulesData = response?.data || [];
      
      // Transform data to match table structure (keep full module object for editing)
      const transformedModules = modulesData.map(module => ({
        id: module.id,
        moduleName: module.menumoduleName || module.moduleName,
        description: module.description || 'N/A',
        status: module.isActive ? 'Active' : 'Inactive',
        createdDate: module.createdOnTime || module.createdOn || module.createdDate,
        // Keep full module object for editing
        _moduleData: module
      }));

      // Sort by ID ascending so IDs appear sequential by default
      transformedModules.sort((a, b) => {
        const idA = Number(a.id) || 0;
        const idB = Number(b.id) || 0;
        return idA - idB;
      });

      setModules(transformedModules);
    } catch (error) {
      console.error('Error loading modules:', error);
      toast.error(extractErrorMessage(error, 'Failed to load modules'));
      setModules([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateModule = async (moduleData) => {
    try {
      setCreating(true);
      let response;
      
      if (editingModule) {
        // Update existing module
        response = await masterMenuModuleService.updateModule(editingModule.id, moduleData);
        if (response?.data) {
          toast.success('Module updated successfully!');
        } else {
          throw new Error('Failed to update module');
        }
      } else {
        // Create new module
        response = await masterMenuModuleService.createModule(moduleData);
        if (response?.data) {
          toast.success('Module created successfully!');
        } else {
          throw new Error('Failed to create module');
        }
      }
      
      setShowCreateModal(false);
      setEditingModule(null);
      await loadModules(); // Refresh the table
    } catch (error) {
      console.error(`Error ${editingModule ? 'updating' : 'creating'} module:`, error);
      toast.error(extractErrorMessage(error, `Failed to ${editingModule ? 'update' : 'create'} module`));
    } finally {
      setCreating(false);
    }
  };

  const handleEditModule = (module) => {
    setEditingModule(module._moduleData || module);
    setShowCreateModal(true);
  };

  const handleDeleteClick = (module) => {
    setModuleToDelete(module);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!moduleToDelete) return;
    
    try {
      setDeleting(true);
      const response = await masterMenuModuleService.deleteModule(moduleToDelete.id);
      
      if (response?.data || response) {
        toast.success('Module deleted successfully (set to inactive)');
        setShowDeleteConfirm(false);
        setModuleToDelete(null);
        await loadModules(); // Refresh the table
      } else {
        throw new Error('Failed to delete module');
      }
    } catch (error) {
      console.error('Error deleting module:', error);
      toast.error(extractErrorMessage(error, 'Failed to delete module'));
    } finally {
      setDeleting(false);
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'inactive':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const columns = [
    {
      key: 'id',
      label: 'ID',
      render: (value) => (
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {value}
        </span>
      )
    },
    {
      key: 'moduleName',
      label: 'Module Name',
      render: (value) => (
        <div className="font-semibold text-gray-900 dark:text-gray-100">
          {value || 'N/A'}
        </div>
      )
    },
    {
      key: 'description',
      label: 'Description',
      render: (value) => (
        <div className="text-sm text-gray-600 dark:text-gray-400 max-w-md">
          {value || 'N/A'}
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <Badge variant="outline" className={getStatusBadgeColor(value)}>
          {value || 'N/A'}
        </Badge>
      )
    },
    {
      key: 'createdDate',
      label: 'Created Date',
      render: (value) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {value ? new Date(value).toLocaleDateString() : 'N/A'}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            title="Edit Module"
            onClick={() => handleEditModule(row)}
            className="text-blue-600 hover:text-blue-700"
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            title="Delete Module"
            onClick={() => handleDeleteClick(row)}
            className="text-red-600 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-8 sm:py-12 px-4 sm:px-6 overflow-hidden">
        {/* Background */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(/background-image.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            filter: 'blur(2px)',
            opacity: 0.3
          }}
        ></div>
        <div className="absolute inset-0 bg-white/70 dark:bg-gray-900/70"></div>

        <div className="container mx-auto relative z-10">
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
            <div className="space-y-2 sm:space-y-3">
              <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-primary/10 border border-primary/20 rounded-full">
                <span className="text-xs sm:text-sm font-semibold text-primary">Administration</span>
              </div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground drop-shadow-lg">
                Module Creation
              </h1>
              <p className="text-sm sm:text-base lg:text-lg text-muted-foreground">
                Create and manage application modules
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
              <Button
                className="gradient-primary text-primary-foreground shadow-elegant hover:shadow-glow w-full sm:w-auto"
                onClick={() => setShowCreateModal(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Module
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <div className="p-4 sm:p-6 relative">
        {/* Modules Table */}
        <Card className="group hover:shadow-luxury transition-smooth border-2 hover:border-primary/30 overflow-hidden relative gradient-card">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          <CardHeader className="relative z-10">
            <div className="flex items-center gap-3 justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-primary to-accent shadow-glow">
                  <Package className="h-5 w-5 text-primary-foreground" />
                </div>
                <div>
                  <CardTitle>All Modules</CardTitle>
                  <CardDescription>{modules.length} modules registered</CardDescription>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="relative z-10">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <p className="text-muted-foreground mt-3">Loading modules...</p>
              </div>
            ) : modules.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8">
                <Package className="h-12 w-12 text-gray-400 mb-4" />
                <p className="text-muted-foreground">No modules found</p>
              </div>
            ) : (
              <DataTable
                data={modules}
                columns={columns}
                pagination={false}
                searchable={false}
                sortable={true}
                itemsPerPage={10}
                showItemsPerPageSelector={false}
              />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Module Modal */}
      <CreateModuleModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setEditingModule(null);
        }}
        onSubmit={handleCreateModule}
        loading={creating}
        editingModule={editingModule}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setModuleToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Module"
        message={`Are you sure you want to delete the module "${moduleToDelete?.moduleName}"? This will set it to inactive.`}
        confirmText={deleting ? "Deleting..." : "Delete"}
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default ModuleCreation;

